import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
const MAX_SPARKLINE_POINTS = 60;

function BtcPricePanel({ livePrice, formatPrice, btcPriceData }) {
  const { t } = useTranslation();
  const [priceHistory, setPriceHistory] = useState([]);
  const prevPriceRef = useRef(null);

  // Accumulate price points for sparkline
  useEffect(() => {
    if (livePrice == null || livePrice === 0) return;

    setPriceHistory((prev) => {
      // Skip duplicate consecutive prices
      if (prev.length > 0 && prev[prev.length - 1] === livePrice) return prev;
      const next = [...prev, livePrice];
      if (next.length > MAX_SPARKLINE_POINTS) next.shift();
      return next;
    });

    prevPriceRef.current = livePrice;
  }, [livePrice]);

  const change24h = btcPriceData?.change24hPct ?? null;
  const isUp = change24h != null ? change24h >= 0 : true;
  const changeColor = isUp ? 'var(--green)' : 'var(--red)';

  const sparklinePath = buildSparklinePath(priceHistory, 400, 50);

  return (
    <div className="stats-panel stats-btc-panel">
      <div className="stats-panel-header">
        <span className="stats-panel-title">{t('stats.btcPrice')}</span>
      </div>

      <div className="stats-btc-price-row">
        <span className="stats-btc-price">
          {livePrice ? formatPrice(livePrice) : '...'}
        </span>
        {change24h != null && (
          <span className="stats-btc-change" style={{ color: changeColor }}>
            {isUp ? '+' : ''}{change24h.toFixed(2)}%
          </span>
        )}
      </div>

      <div className="stats-btc-sparkline">
        {priceHistory.length >= 2 ? (
          <svg viewBox="0 0 400 50" preserveAspectRatio="none">
            <defs>
              <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={isUp ? '#22c55e' : '#ef4444'} stopOpacity="0.3" />
                <stop offset="100%" stopColor={isUp ? '#22c55e' : '#ef4444'} stopOpacity="0.02" />
              </linearGradient>
            </defs>
            <path d={sparklinePath.area} fill="url(#sparkGrad)" />
            <path
              d={sparklinePath.line}
              fill="none"
              stroke={isUp ? '#22c55e' : '#ef4444'}
              strokeWidth="1.5"
            />
          </svg>
        ) : (
          <div className="stats-btc-spark-empty">{t('stats.collectingData')}</div>
        )}
      </div>

    </div>
  );
}

export function buildSparklinePath(prices, width, height) {
  if (prices.length < 2) return { line: '', area: '' };

  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  const padding = 2;
  const usableHeight = height - padding * 2;

  const points = prices.map((p, i) => {
    const x = (i / (prices.length - 1)) * width;
    const y = padding + usableHeight - ((p - min) / range) * usableHeight;
    return { x, y };
  });

  const line = points.map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt.x.toFixed(1)},${pt.y.toFixed(1)}`).join(' ');
  const area = line + ` L${width},${height} L0,${height} Z`;

  return { line, area };
}

export default BtcPricePanel;
